import React, { useState, useEffect, useRef } from 'react';
import FormSection from '../FormSection';
import FormField from '../FormField';
import Input from '../Input';
import SearchableSelect from '../SearchableSelect';
import Textarea from '../Textarea';
import ImageModal from '../ImageModal';
import Modal from '../Modal';
import CustomSelect from '../CustomSelect';
import { Speaker } from '../../types/profile.types';
import { thematicAxes, subejesPorEje, countries } from '../../data/profileData';
import { validateFileSize, validateMultipleFiles, MAX_FILE_SIZE_IMAGES, MAX_FILE_SIZE_DOCUMENTS } from '../../utils/fileValidation';
import { useFileError } from '../../contexts/FileErrorContext';
import { useUploadProgress } from '../../contexts/UploadProgressContext';
import { useTimeout } from '../../contexts/TimeoutContext';
import { uploadToSupabase } from '../../services/supabase';
import { handleRateLimitError } from '../../utils/rateLimitHandler';
import { handleUploadError } from '../../utils/uploadErrorHandler';
import { CheckCircleIcon, XCircleIcon, AlertTriangleIcon } from '../icons/AdminIcons';
import '../CheckboxGroup.css';
import './SpeakersSection.css';
import './ThematicAxesSection.css';

// Helper function para obtener el estado del orador (igual que en AdminPanel)
const getSpeakerStatusHelper = (speaker: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
  if (!speaker) return { completionStatus: 'incomplete', hasRequestApproval: false };
  
  const hasFirstName = speaker.firstName && speaker.firstName.toString().trim() !== '';
  const hasLastName = speaker.lastName && speaker.lastName.toString().trim() !== '';
  const hasPosition = speaker.position && speaker.position.toString().trim() !== '';
  const hasOrganization = speaker.organization && speaker.organization.toString().trim() !== '';
  const hasNationality = speaker.nationality && speaker.nationality.toString().trim() !== '';
  const hasDescription = speaker.description && speaker.description.toString().trim() !== '';
  const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
  const hasPhoto = speaker.photo && speaker.photo.toString().trim() !== '';
  const isComplete = hasFirstName && hasLastName && hasPosition && hasOrganization && hasNationality && hasDescription && hasThematicAxes && hasPhoto;
  
  return {
    completionStatus: isComplete ? 'complete' : 'incomplete',
    hasRequestApproval: speaker.requestApproval === true
  };
};

// Función para capitalizar solo la primera letra (mantener el resto como está, incluyendo espacios)
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return '';
  // Encontrar el primer carácter que no sea espacio
  const firstNonSpaceIndex = str.search(/\S/);
  if (firstNonSpaceIndex === -1) {
    // Si solo hay espacios, retornar como está
    return str;
  }
  // Capitalizar solo la primera letra no-espacio, mantener todo lo demás (incluyendo espacios)
  return str.slice(0, firstNonSpaceIndex) + 
         str.charAt(firstNonSpaceIndex).toUpperCase() + 
         str.slice(firstNonSpaceIndex + 1);
};

interface SpeakersSectionProps {
  speakers: Speaker[];
  onChange: (speakers: Speaker[]) => void;
  readOnly?: boolean;
  hideTitle?: boolean;
  initialExpandedSpeakerId?: string;
  showApprovalBadge?: boolean;
  onApprovalStatusChange?: (speakerId: string, status: 'pending' | 'approved' | 'rejected') => void;
  userId?: number;
}

interface SpeakerCardProps {
  speaker: Speaker;
  onUpdate: (id: string, field: keyof Speaker, value: any) => void;
  onRemove: (id: string) => void;
}

interface SpeakerCardProps {
  speaker: Speaker;
  onUpdate: (id: string, field: keyof Speaker, value: any) => void;
  onRemove: (id: string) => void;
  readOnly?: boolean;
  initialExpanded?: boolean;
  showApprovalBadge?: boolean;
  onApprovalStatusChange?: (speakerId: string, status: 'pending' | 'approved' | 'rejected') => void;
  userId?: number;
}

const SpeakerCard: React.FC<SpeakerCardProps> = ({ speaker, onUpdate, onRemove, readOnly = false, initialExpanded = false, showApprovalBadge = false, onApprovalStatusChange, userId }) => {
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [organizationLogoPreview, setOrganizationLogoPreview] = useState<string | null>(null);
  const [filePreviews, setFilePreviews] = useState<Array<{ name: string; data: string }>>([]);
  const [visibleTooltip, setVisibleTooltip] = useState<number | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showOrganizationLogoModal, setShowOrganizationLogoModal] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(initialExpanded);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const organizationLogoFileInputRef = useRef<HTMLInputElement>(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const { showFileSizeError } = useFileError();
  const { startUpload, updateProgress, finishUpload, setCurrentFileName } = useUploadProgress();
  const { showTimeout } = useTimeout();
  const [urlErrors, setUrlErrors] = useState<{ linkedin: string; instagram: string; twitter: string }>({
    linkedin: '',
    instagram: '',
    twitter: ''
  });
  const [biographyError, setBiographyError] = useState<string>('');

  // Validar URLs y biografía continuamente para que los errores persistan mientras haya contenido inválido
  useEffect(() => {
    // Validar biografía - SOLO validar URL, NO mostrar mensaje de obligatorio
    // La validación de obligatorio se hace en el formulario completo, pero sin mostrar mensaje aquí
    const biography = speaker.biography || '';
    // La validación de URL es independiente de LinkedIn
    if (biography.trim() === '') {
      setBiographyError('');
    } else if (!validateUrl(biography)) {
      setBiographyError('Por favor, ingresa una URL válida');
    } else {
      setBiographyError('');
    }

    // Validar URLs de redes sociales
    const newUrlErrors = { ...urlErrors };
    if (speaker.linkedin && speaker.linkedin.trim() && !validateUrl(speaker.linkedin)) {
      newUrlErrors.linkedin = 'Por favor, ingresa una URL válida';
    } else {
      newUrlErrors.linkedin = '';
    }
    if (speaker.instagram && speaker.instagram.trim() && !validateUrl(speaker.instagram)) {
      newUrlErrors.instagram = 'Por favor, ingresa una URL válida';
    } else {
      newUrlErrors.instagram = '';
    }
    if (speaker.twitter && speaker.twitter.trim() && !validateUrl(speaker.twitter)) {
      newUrlErrors.twitter = 'Por favor, ingresa una URL válida';
    } else {
      newUrlErrors.twitter = '';
    }
    setUrlErrors(newUrlErrors);
  }, [speaker.biography, speaker.instagram, speaker.twitter, speaker.linkedin]);

  // Función de validación de URL (igual al form participar)
  const validateUrl = (url: string): boolean => {
    if (url.trim() === '') return true; // Campo opcional
    
    // Si ya tiene protocolo, validar URL completa
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      return urlRegex.test(url);
    }
    
    // Si no tiene protocolo, validar solo dominio
    const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return domainRegex.test(url);
  };

  // Validar Biografía: obligatorio PERO puede estar vacío si LinkedIn está lleno
  const validateBiography = (biography: string, linkedin: string): boolean => {
    const biographyTrimmed = biography.trim();
    const linkedinTrimmed = linkedin.trim();
    
    // Si LinkedIn está lleno, Biografía puede estar vacío
    if (linkedinTrimmed !== '') {
      return true;
    }
    
    // Si LinkedIn está vacío, Biografía es obligatorio
    return biographyTrimmed !== '';
  };

  // Detectar si es mobile o tablet
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    try {
      if (speaker.photo && typeof speaker.photo !== 'string') {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string);
        };
        reader.onerror = () => {
          setPhotoPreview(null);
        };
        reader.readAsDataURL(speaker.photo);
      } else if (typeof speaker.photo === 'string' && speaker.photo) {
        // Validar que sea una URL válida o base64 válido
        // Aceptar: data:image/, http://, https://, o /storage/files/ (proxy URL)
        if (speaker.photo.startsWith('data:image/') || 
            speaker.photo.startsWith('http://') || 
            speaker.photo.startsWith('https://') ||
            speaker.photo.startsWith('/storage/files/')) {
          setPhotoPreview(speaker.photo);
        } else {
          setPhotoPreview(null);
        }
      } else {
        setPhotoPreview(null);
      }
    } catch (error) {
      setPhotoPreview(null);
    }
  }, [speaker.photo]);

  useEffect(() => {
    try {
      if (speaker.organizationLogo && typeof speaker.organizationLogo !== 'string') {
        const reader = new FileReader();
        reader.onloadend = () => {
          setOrganizationLogoPreview(reader.result as string);
        };
        reader.onerror = () => {
          setOrganizationLogoPreview(null);
        };
        reader.readAsDataURL(speaker.organizationLogo);
      } else if (typeof speaker.organizationLogo === 'string' && speaker.organizationLogo) {
        // Validar que sea una URL válida o base64 válido
        // Aceptar: data:image/, http://, https://, o /storage/files/ (proxy URL)
        if (speaker.organizationLogo.startsWith('data:image/') || 
            speaker.organizationLogo.startsWith('http://') || 
            speaker.organizationLogo.startsWith('https://') ||
            speaker.organizationLogo.startsWith('/storage/files/')) {
          setOrganizationLogoPreview(speaker.organizationLogo);
        } else {
          setOrganizationLogoPreview(null);
        }
      } else {
        setOrganizationLogoPreview(null);
      }
    } catch (error) {
      setOrganizationLogoPreview(null);
    }
  }, [speaker.organizationLogo]);

  useEffect(() => {
    const loadFilePreviews = async () => {
      if (!speaker.supportDocuments || !Array.isArray(speaker.supportDocuments)) {
        setFilePreviews([]);
        return;
      }

      const previews: Array<{ name: string; data: string }> = [];
      
      for (const doc of speaker.supportDocuments) {
        try {
          if (doc instanceof File) {
            const reader = new FileReader();
            const preview = await new Promise<string>((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(doc);
            });
            previews.push({ name: doc.name, data: preview });
          } else if (typeof doc === 'string') {
            // Verificar si tiene formato con nombre: "FILENAME:nombre_archivo:DATA:base64data" (formato antiguo)
            if (doc.startsWith('FILENAME:') && doc.includes(':DATA:')) {
              const parts = doc.split(':DATA:');
              const fileNamePart = parts[0].replace('FILENAME:', '');
              const base64Data = parts[1];
              previews.push({ name: fileNamePart, data: base64Data });
            } 
            // Verificar si tiene formato con URL: "FILENAME:nombre_archivo:URL:url" (formato nuevo con WordPress)
            else if (doc.startsWith('FILENAME:') && doc.includes(':URL:')) {
              const parts = doc.split(':URL:');
              const fileNamePart = parts[0].replace('FILENAME:', '');
              const url = parts[1];
              previews.push({ name: fileNamePart, data: url });
            } 
              // Formato base64 sin nombre guardado (archivos antiguos)
            else if (doc.includes(';base64,')) {
              previews.push({ name: `documento_${previews.length + 1}`, data: doc });
            } 
            // URL directa (WordPress u otra)
            else if (doc.startsWith('http://') || doc.startsWith('https://')) {
              const fileName = doc.split('/').pop()?.split('?')[0] || `documento_${previews.length + 1}`;
              previews.push({ name: fileName, data: doc });
            } 
            // Formato desconocido
            else {
              const fileName = doc.split('/').pop() || `documento_${previews.length + 1}`;
              previews.push({ name: fileName, data: doc });
            }
          }
        } catch (error) {
        }
      }
      
      setFilePreviews(previews);
    };

    loadFilePreviews();
  }, [speaker.supportDocuments]);

  // Efecto para manejar el tooltip en el DOM
  useEffect(() => {
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }

    if (visibleTooltip !== null && tooltipPosition !== null) {
      const ejeSeleccionado = thematicAxes[visibleTooltip];
      const subejes = subejesPorEje[ejeSeleccionado.label]?.subejes || [];
      
      if (subejes.length === 0) return;

      // Detectar si está en modo oscuro
      const isDarkMode = document.documentElement.classList.contains('dark-theme');

      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${tooltipPosition.left}px !important;
        top: ${tooltipPosition.top}px !important;
        z-index: 999999 !important;
        background-color: ${isDarkMode ? '#0f172a' : '#ffffff'} !important;
        border: 2px solid #CE2B15 !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      `;

      const titleDiv = document.createElement('div');
      titleDiv.style.cssText = `font-weight: 700; color: ${isDarkMode ? '#ffffff' : '#1e293b'}; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${isDarkMode ? '#334155' : '#e5e7eb'};`;
      titleDiv.textContent = 'Incluye:';

      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';

      subejes.forEach((subeje) => {
        const li = document.createElement('li');
        li.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;`;
        
        const bullet = document.createElement('span');
        bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
        bullet.textContent = '•';
        
        li.appendChild(bullet);
        li.appendChild(document.createTextNode(subeje));
        ul.appendChild(li);
      });

      tooltipDiv.appendChild(titleDiv);
      tooltipDiv.appendChild(ul);
      
      if (document.body) {
        document.body.appendChild(tooltipDiv);
        tooltipRef.current = tooltipDiv;
      }

      return () => {
        if (tooltipRef.current && tooltipRef.current.parentNode) {
          tooltipRef.current.parentNode.removeChild(tooltipRef.current);
          tooltipRef.current = null;
        }
      };
    }
  }, [visibleTooltip, tooltipPosition]);

  // Cerrar tooltip al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (!isMobileOrTablet || visibleTooltip === null) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('.checkbox-item')) {
          setVisibleTooltip(null);
          setTooltipPosition(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOrTablet, visibleTooltip]);

  const handlePhotoChange = async (file: File) => {
    // Validar tamaño de foto antes de agregarla
    const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'La foto excede el tamaño máximo permitido.');
      return;
    }
    
    try {
      // Mostrar preview inmediato
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Iniciar progreso de subida

      startUpload(file.name, 1);
      
      // Subir inmediatamente a Supabase

      const photoUrl = await uploadToSupabase(file, {
        folder: 'perfil-institucional/speakers/photos',
        onProgress: (progress) => {
          updateProgress(progress, 0);
        }
      });
      
      // Finalizar progreso
      finishUpload();
      
      // Log para debugging
      
      // Actualizar con la URL de Supabase
      onUpdate(speaker.id, 'photo', photoUrl);
      
      // Forzar actualización del preview inmediatamente
      if (photoUrl) {
        setPhotoPreview(photoUrl);
      }
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida de la foto');
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso para otros tipos de error
    }
  };

  const handlePhotoChangeClick = () => {
    photoFileInputRef.current?.click();
  };

  const handlePhotoDelete = async () => {
    // Limpiar el preview
    setPhotoPreview(null);
    // Actualizar el orador sin foto
    onUpdate(speaker.id, 'photo', undefined);
  };

  const handleOrganizationLogoChange = async (file: File) => {
    // Validar tipo de archivo - solo PNG y formatos vectoriales editables (sin PDF)
    const allowedTypes = [
      'image/png',
      'image/svg+xml',
      'image/svg',
      'application/postscript',
      'application/illustrator'
    ];
    const allowedExtensions = ['.png', '.svg', '.ai', '.eps'];

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = allowedTypes.includes(file.type) || 
                        allowedExtensions.includes(fileExtension) ||
                        file.name.toLowerCase().endsWith('.svg') ||
                        file.name.toLowerCase().endsWith('.ai') ||
                        file.name.toLowerCase().endsWith('.eps');

    if (!isValidType) {
      showFileSizeError('El logo debe estar en formato PNG o formato de curvas editable (SVG, AI, EPS).', true);
      return;
    }

    // Validar tamaño de logo antes de agregarlo
    const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'El logo excede el tamaño máximo permitido.');
      return;
    }
    
    try {
      // Mostrar preview inmediato
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setOrganizationLogoPreview(previewUrl);
        // Si el modal está abierto, mantenerlo abierto con la nueva imagen
        if (showOrganizationLogoModal) {
          // El modal se actualizará automáticamente porque imageUrl cambia
        }
      };
      reader.readAsDataURL(file);
      
      // Iniciar progreso de subida
      startUpload(file.name, 1);
      
      // Subir inmediatamente a Supabase
      const logoUrl = await uploadToSupabase(file, {
        folder: 'perfil-institucional/speakers/organization-logos',
        onProgress: (progress) => {
          updateProgress(progress, 0);
        }
      });
      
      // Finalizar progreso
      finishUpload();
      
      // Log para debugging
      
      // Actualizar con la URL de Supabase
      onUpdate(speaker.id, 'organizationLogo', logoUrl);
      
      // Forzar actualización del preview inmediatamente
      if (logoUrl) {
        setOrganizationLogoPreview(logoUrl);
      }
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida del logo de la organización');
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso para otros tipos de error
    }
  };

  const handleOrganizationLogoChangeClick = () => {
    organizationLogoFileInputRef.current?.click();
  };

  const handleOrganizationLogoDelete = async () => {
    // Limpiar el preview
    setOrganizationLogoPreview(null);
    // Actualizar el orador sin logo
    onUpdate(speaker.id, 'organizationLogo', undefined);
  };

  const handleFileChange = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const newFiles = Array.from(files);
    
    // Validar tamaño de archivos antes de agregarlos
    const validation = validateMultipleFiles(newFiles, MAX_FILE_SIZE_DOCUMENTS, 'document');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'El archivo excede el tamaño máximo permitido.');
      return;
    }
    
    try {
      // Iniciar progreso de subida
      startUpload(newFiles[0].name, newFiles.length);
      
      // Subir archivos de forma secuencial (uno después del otro)
      const uploadedUrls: string[] = [];
      for (let index = 0; index < newFiles.length; index++) {
        const file = newFiles[index];
        // Actualizar el nombre del archivo actual
        setCurrentFileName(file.name);
        const docUrl = await uploadToSupabase(file, {
          folder: 'perfil-institucional/speakers/documents',
          onProgress: (progress) => {
            // Calcular progreso total considerando todos los archivos
            // Cada archivo completo representa (100 / totalArchivos)%
            const baseProgress = (index / newFiles.length) * 100;
            const fileProgress = (progress / newFiles.length);
            const totalProgress = baseProgress + fileProgress;
            updateProgress(totalProgress, index);
          }
        });
        // Log para debugging
        
        // Guardar con formato: "FILENAME:nombre_archivo:URL:url"
        uploadedUrls.push(`FILENAME:${file.name}:URL:${docUrl}`);
      }
      
      // Finalizar progreso
      finishUpload();
      
      const currentDocs = Array.isArray(speaker.supportDocuments) ? speaker.supportDocuments : [];
      
      // Log para debugging
      
      // Agregar las URLs al estado
      onUpdate(speaker.id, 'supportDocuments', [...currentDocs, ...uploadedUrls]);
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso para otros tipos de error
    }
  };

  const handleRemoveFile = (index: number) => {
    const currentDocs = Array.isArray(speaker.supportDocuments) ? speaker.supportDocuments : [];
    const updatedDocs = currentDocs.filter((_, i) => i !== index);
    onUpdate(speaker.id, 'supportDocuments', updatedDocs.length > 0 ? updatedDocs : undefined);
  };

  const handleBlur = (field: keyof Speaker, value: string) => {
    // Capitalizar primera letra solo cuando el usuario termina de escribir (onBlur)
    if (field === 'firstName' || field === 'lastName' || field === 'nationality' || 
        field === 'organization' || field === 'position') {
      const capitalized = capitalizeFirstLetter(value);
      if (capitalized !== value) {
        onUpdate(speaker.id, field, capitalized);
      }
    }
  };

  return (
    <div className="speaker-card" style={{ position: 'relative', overflow: 'visible' }}>
      {/* Badges de estado en el borde superior derecho (solo en readonly del popup) */}
      {readOnly && showApprovalBadge && (() => {
        const statusInfo = getSpeakerStatusHelper(speaker);
        // Solo mostrar selector de aprobación si está completo o tiene solicitud de aprobación
        const shouldShowApprovalSelector = statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval;
        
        return (
          <div className="admin-participation-card-status-badges" style={{ marginRight: '10px' }}>
            {/* Badge de estado de completitud - siempre se muestra si corresponde */}
            <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
              {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
              {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
            </span>
            {/* Badge de solicitado - siempre se muestra si corresponde */}
            {statusInfo.hasRequestApproval && (
              <span className="participation-status-badge status-requested">
                <AlertTriangleIcon width={12} height={12} /> Solicitado
              </span>
            )}
            {/* Selector de estado de aprobación - solo se muestra si está completo o tiene solicitud */}
            {shouldShowApprovalSelector && onApprovalStatusChange && (
              <div style={{ minWidth: '120px', maxWidth: '140px', flexShrink: 0 }}>
                <CustomSelect
                  className="admin-filter-select admin-filter-select-small"
                  value={speaker.approval_status || 'pending'}
                  onChange={(newStatus) => {
                    if (onApprovalStatusChange) {
                      onApprovalStatusChange(speaker.id, newStatus as 'pending' | 'approved' | 'rejected');
                    }
                  }}
                  options={[
                    { value: 'pending', label: 'Pendiente', color: '#3b82f6' },
                    { value: 'approved', label: 'Aprobado', color: '#10b981' },
                    { value: 'rejected', label: 'Rechazado', color: '#ef4444' }
                  ]}
                />
              </div>
            )}
          </div>
        );
      })()}
      <div className="speaker-header">
        <h3>{speaker.firstName || speaker.lastName ? `${speaker.firstName} ${speaker.lastName}`.trim() || 'Nuevo Orador' : 'Nuevo Orador'}</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            className="minimize-button"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Minimizar' : 'Expandir'}
            style={{
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '14px',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500
            }}
          >
            {isExpanded ? '−' : '+'}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="remove-button"
              onClick={() => setShowDeleteModal(true)}
            >
              Eliminar
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
      <div className="speaker-card-content">
        <div className="form-row">
        <FormField label="Foto" required>
          <div className="photo-upload-container">
            <div className="photo-preview">
              {photoPreview ? (
                <img 
                  src={photoPreview} 
                  alt="Foto del orador" 
                  className="photo-preview-image"
                  onClick={() => setShowImageModal(true)}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <div className="photo-placeholder">
                  <p>Sin foto</p>
                </div>
              )}
            </div>
            {photoPreview && (
              <ImageModal
                isOpen={showImageModal}
                imageUrl={photoPreview}
                alt="Foto del orador"
                onClose={() => setShowImageModal(false)}
                onSave={async (editedImageUrl) => {
                  // Convertir la URL del blob a File
                  try {
                    // La URL puede ser blob, data, o una URL de WordPress
                    // Si es una URL de WordPress, necesitamos convertirla a blob primero
                    if (!editedImageUrl) {

                      return;
                    }

                    let blob: Blob;
                    
                    if (editedImageUrl.startsWith('blob:') || editedImageUrl.startsWith('data:')) {
                      // Es un blob o data URL, hacer fetch directamente
                      const response = await fetch(editedImageUrl);
                      if (!response.ok) {
                        throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                      }
                      blob = await response.blob();
                    } else {
                      // Es una URL de WordPress, usar el proxy para evitar CORS
                      let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
                      proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
                      const proxyUrl = `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(editedImageUrl)}`;
                      const response = await fetch(proxyUrl);
                      if (!response.ok) {
                        throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                      }
                      blob = await response.blob();
                    }

                    // Extraer el nombre original del archivo de la URL original
                    const getOriginalFileName = (url: string): string => {
                      try {
                        // Si es una URL de WordPress, extraer el nombre del archivo
                        if (url && url.includes('/')) {
                          const urlParts = url.split('/');
                          const fileName = urlParts[urlParts.length - 1];
                          // Remover parámetros de query si existen
                          const fileNameWithoutParams = fileName.split('?')[0];
                          // Si tiene nombre válido, usarlo
                          if (fileNameWithoutParams && fileNameWithoutParams.includes('.')) {
                            return fileNameWithoutParams;
                          }
                        }
                        // Si es un blob o data URL, usar nombre por defecto
                        return 'photo.jpg';
                      } catch {
                        return 'photo.jpg';
                      }
                    };

                    const originalFileName = getOriginalFileName(photoPreview || '');
                    // Extraer nombre y extensión
                    const lastDotIndex = originalFileName.lastIndexOf('.');
                    let fileNameWithoutExt = originalFileName;
                    let fileExtension = '.jpg';
                    
                    if (lastDotIndex > 0) {
                      fileNameWithoutExt = originalFileName.substring(0, lastDotIndex);
                      fileExtension = originalFileName.substring(lastDotIndex);
                    }
                    
                    // Agregar "-edited" antes de la extensión
                    const editedFileName = `${fileNameWithoutExt}-edited${fileExtension}`;

                    const file = new File([blob], editedFileName, { type: blob.type || 'image/jpeg' });
                    
                    // Actualizar el preview
                    setPhotoPreview(editedImageUrl);
                    
                    // Subir la imagen editada
                    await handlePhotoChange(file);
                  } catch (error) {

                    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

                    // Error silencioso - no mostrar diálogo al usuario
                  }
                }}
                allowEdit={!readOnly}
                isPhoto={true}
                onPhotoChange={handlePhotoChangeClick}
                onPhotoDelete={handlePhotoDelete}
              />
            )}
            <input
              ref={photoFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handlePhotoChange(file);
                }
                // Resetear el input
                if (e.target) {
                  e.target.value = '';
                }
              }}
              style={{ display: 'none' }}
            />
            {!readOnly && (
              <button 
                className="photo-upload-btn"
                onClick={() => {
                  if (photoPreview) {
                    setShowImageModal(true);
                  } else {
                    photoFileInputRef.current?.click();
                  }
                }}
              >
                {photoPreview ? 'Editar Foto' : 'Subir Foto'}
              </button>
            )}
          </div>
        </FormField>
        <FormField label="Logo de la organización">
          <div className="photo-upload-container">
            <div className="photo-preview">
              {organizationLogoPreview ? (
                <img 
                  src={organizationLogoPreview} 
                  alt="Logo de la organización" 
                  className="photo-preview-image"
                  onClick={() => setShowOrganizationLogoModal(true)}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <div className="photo-placeholder">
                  <p>Sin logo</p>
                </div>
              )}
            </div>
            {organizationLogoPreview && (
              <ImageModal
                isOpen={showOrganizationLogoModal}
                imageUrl={organizationLogoPreview}
                alt="Logo de la organización"
                onClose={() => setShowOrganizationLogoModal(false)}
                onSave={async (editedImageUrl) => {
                  // Convertir la URL del blob a File
                  try {
                    // La URL puede ser blob, data, o una URL de WordPress
                    // Si es una URL de WordPress, necesitamos convertirla a blob primero
                    if (!editedImageUrl) {

                      return;
                    }

                    let blob: Blob;
                    
                    if (editedImageUrl.startsWith('blob:') || editedImageUrl.startsWith('data:')) {
                      // Es un blob o data URL, hacer fetch directamente
                      const response = await fetch(editedImageUrl);
                      if (!response.ok) {
                        throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                      }
                      blob = await response.blob();
                    } else {
                      // Es una URL de WordPress, usar el proxy para evitar CORS
                      let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
                      proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
                      const proxyUrl = `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(editedImageUrl)}`;
                      const response = await fetch(proxyUrl);
                      if (!response.ok) {
                        throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                      }
                      blob = await response.blob();
                    }

                    // Extraer el nombre original del archivo de la URL original
                    const getOriginalFileName = (url: string): string => {
                      try {
                        // Si es una URL de WordPress, extraer el nombre del archivo
                        if (url && url.includes('/')) {
                          const urlParts = url.split('/');
                          const fileName = urlParts[urlParts.length - 1];
                          // Remover parámetros de query si existen
                          const fileNameWithoutParams = fileName.split('?')[0];
                          // Si tiene nombre válido, usarlo
                          if (fileNameWithoutParams && fileNameWithoutParams.includes('.')) {
                            return fileNameWithoutParams;
                          }
                        }
                        // Si es un blob o data URL, usar nombre por defecto
                        return 'logo.png';
                      } catch {
                        return 'logo.png';
                      }
                    };

                    const originalFileName = getOriginalFileName(organizationLogoPreview || '');
                    // Extraer nombre y extensión
                    const lastDotIndex = originalFileName.lastIndexOf('.');
                    let fileNameWithoutExt = originalFileName;
                    let fileExtension = '.png';
                    
                    if (lastDotIndex > 0) {
                      fileNameWithoutExt = originalFileName.substring(0, lastDotIndex);
                      fileExtension = originalFileName.substring(lastDotIndex);
                    }
                    
                    // Agregar "-edited" antes de la extensión
                    const editedFileName = `${fileNameWithoutExt}-edited${fileExtension}`;

                    const file = new File([blob], editedFileName, { type: blob.type || 'image/png' });
                    
                    // Actualizar el preview
                    setOrganizationLogoPreview(editedImageUrl);
                    
                    // Subir la imagen editada
                    await handleOrganizationLogoChange(file);
                  } catch (error) {

                    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

                    // Error silencioso - no mostrar diálogo al usuario
                  }
                }}
                allowEdit={!readOnly}
                isPhoto={true}
                onPhotoChange={handleOrganizationLogoChangeClick}
                onPhotoDelete={handleOrganizationLogoDelete}
              />
            )}
            <input
              ref={organizationLogoFileInputRef}
              type="file"
              accept="image/png,image/svg+xml,image/svg,.svg,.ai,.eps,application/postscript,application/illustrator"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleOrganizationLogoChange(file);
                }
                // Resetear el input
                if (e.target) {
                  e.target.value = '';
                }
              }}
              style={{ display: 'none' }}
            />
            {!readOnly && (
              <button 
                className="photo-upload-btn"
                onClick={() => {
                  if (organizationLogoPreview) {
                    setShowOrganizationLogoModal(true);
                  } else {
                    organizationLogoFileInputRef.current?.click();
                  }
                }}
              >
                {organizationLogoPreview ? 'Editar Logo' : 'Subir Logo'}
              </button>
            )}
            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', fontStyle: 'italic' }}>
              En caso de que este campo quede vacío, se usará el logo de tu organización para las comunicaciones.
            </p>
          </div>
        </FormField>
      </div>

      <div className="form-row">
        <FormField label="Nombre" required>
          <Input
            value={speaker.firstName}
            onChange={(e) => onUpdate(speaker.id, 'firstName', e.target.value)}
            onBlur={(e) => handleBlur('firstName', e.target.value)}
            placeholder="Nombre"
            disabled={readOnly}
          />
        </FormField>
        <FormField label="Apellido" required>
          <Input
            value={speaker.lastName}
            onChange={(e) => onUpdate(speaker.id, 'lastName', e.target.value)}
            onBlur={(e) => handleBlur('lastName', e.target.value)}
            placeholder="Apellido"
            disabled={readOnly}
          />
        </FormField>
      </div>

      <div className="form-row">
        <FormField label="Nacionalidad" required>
          <SearchableSelect
            value={speaker.nationality || ''}
            onChange={(value) => onUpdate(speaker.id, 'nationality', value)}
            options={countries.map(c => c.name)}
            placeholder="Selecciona un país"
            allowSearch={true}
            disabled={readOnly}
          />
        </FormField>
        <FormField label="Organización" required>
          <Input
            value={speaker.organization}
            onChange={(e) => onUpdate(speaker.id, 'organization', e.target.value)}
            onBlur={(e) => handleBlur('organization', e.target.value)}
            placeholder="Nombre de la organización"
            disabled={readOnly}
          />
        </FormField>
      </div>

      <div className="form-row">
        <FormField label="Cargo o especialidad" required>
          <Input
            value={speaker.position}
            onChange={(e) => onUpdate(speaker.id, 'position', e.target.value)}
            onBlur={(e) => handleBlur('position', e.target.value)}
            placeholder="Ej: Director de Innovación"
            disabled={readOnly}
          />
        </FormField>
        <FormField 
          label="Biografía" 
          required={!speaker.linkedin || speaker.linkedin.trim() === ''}
          error={biographyError}
        >
          <Input
            type="url"
            value={speaker.biography || ''}
            onChange={(e) => {
              const value = e.target.value;
              onUpdate(speaker.id, 'biography', value);
            }}
            onBlur={(e) => {
              const value = e.target.value.trim();
              // Si el usuario escribió algo y no tiene protocolo, agregar https://
              if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
                if (domainRegex.test(value)) {
                  onUpdate(speaker.id, 'biography', `https://${value}`);
                }
              }
              // Solo validar URL, no mostrar mensaje de obligatorio
              // La validación de obligatorio se hace en el formulario completo
              if (value && !validateUrl(value)) {
                setBiographyError('Por favor, ingresa una URL válida');
              } else {
                setBiographyError('');
              }
            }}
            placeholder="https://ejemplo.com/biografia"
            error={!!biographyError}
            disabled={readOnly}
          />
          {(!speaker.linkedin || speaker.linkedin.trim() === '') && (
            <p style={{ 
              fontSize: '12px', 
              color: '#64748b', 
              marginTop: '4px',
              fontStyle: 'italic',
              fontFamily: "'Montserrat', sans-serif"
            }}>
              En caso de ingresar el LinkedIn del orador, este campo puede quedar vacío
            </p>
          )}
        </FormField>
      </div>

      <div className="form-row">
        <FormField label="LinkedIn" error={urlErrors.linkedin}>
          <Input
            type="url"
            value={speaker.linkedin || ''}
            onChange={(e) => {
              const value = e.target.value;
              onUpdate(speaker.id, 'linkedin', value);
            }}
            onBlur={(e) => {
              const value = e.target.value.trim();
              // Si el usuario escribió algo y no tiene protocolo, agregar https://
              if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
                if (domainRegex.test(value)) {
                  onUpdate(speaker.id, 'linkedin', `https://${value}`);
                }
              }
              // NO validar biografía aquí - la validación de URL es independiente de LinkedIn
              // La validación de obligatorio se hace en el formulario completo
            }}
            placeholder="https://linkedin.com/in/usuario"
            error={!!urlErrors.linkedin}
            disabled={readOnly}
          />
        </FormField>
        <FormField label="Instagram" error={urlErrors.instagram}>
          <Input
            type="url"
            value={speaker.instagram || ''}
            onChange={(e) => {
              const value = e.target.value;
              onUpdate(speaker.id, 'instagram', value);
            }}
            onBlur={(e) => {
              const value = e.target.value.trim();
              // Si el usuario escribió algo y no tiene protocolo, agregar https://
              if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
                if (domainRegex.test(value)) {
                  onUpdate(speaker.id, 'instagram', `https://${value}`);
                }
              }
            }}
            placeholder="https://instagram.com/usuario"
            error={!!urlErrors.instagram}
            disabled={readOnly}
          />
        </FormField>
      </div>

      <div className="form-row">
        <FormField label="Twitter/X" error={urlErrors.twitter}>
          <Input
            type="url"
            value={speaker.twitter || ''}
            onChange={(e) => {
              const value = e.target.value;
              onUpdate(speaker.id, 'twitter', value);
            }}
            onBlur={(e) => {
              const value = e.target.value.trim();
              // Si el usuario escribió algo y no tiene protocolo, agregar https://
              if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
                if (domainRegex.test(value)) {
                  onUpdate(speaker.id, 'twitter', `https://${value}`);
                }
              }
            }}
            placeholder="https://twitter.com/usuario o https://x.com/usuario"
            error={!!urlErrors.twitter}
            disabled={readOnly}
          />
        </FormField>
      </div>

      <FormField label="Minibiografía" required className="speaker-minibiography-field">
        <Textarea
          value={speaker.description}
          onChange={(e) => onUpdate(speaker.id, 'description', e.target.value)}
          placeholder="Minibiografía del orador..."
          rows={4}
          maxLength={500}
          showCharCount={true}
          disabled={readOnly}
        />
      </FormField>

      <FormField 
        label="Temática de interés para su exposición" 
        required
        helpText={
          <>
            Selecciona uno o varios ejes temáticos (Conoce más de cada eje en{' '}
            <a 
              href="https://smartcityexposantiagodelestero.com/tematicas" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#CE2B15', 
                textDecoration: 'none',
                fontWeight: 600
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Temáticas
            </a>
            )
          </>
        }
      >
        <div className="checkbox-group thematic-axes-two-columns">
          {/* Selector "Seleccionar todos" */}
          <label 
            className="checkbox-item select-all-checkbox"
            style={{
              fontWeight: 600,
              marginBottom: '8px',
              width: '100%'
            }}
          >
            <input
              type="checkbox"
              checked={
                (speaker.thematicAxes || []).length > 0 &&
                (speaker.thematicAxes || []).length === thematicAxes.length
              }
              onChange={(e) => {
                if (readOnly) return;
                const currentAxes = speaker.thematicAxes || [];
                if (e.target.checked) {
                  // Seleccionar todos
                  const allAxes = thematicAxes.map(axis => axis.value);
                  onUpdate(speaker.id, 'thematicAxes', allAxes);
                } else {
                  // Deseleccionar todos
                  onUpdate(speaker.id, 'thematicAxes', []);
                }
              }}
              disabled={readOnly}
            />
            <span className="checkbox-label">Seleccionar todos</span>
          </label>
          {thematicAxes.map((option, index) => {
            const subejes = subejesPorEje[option.label]?.subejes || [];
            const hasSubejes = subejes.length > 0;
            const isChecked = speaker.thematicAxes?.includes(option.value) || false;
            
            return (
              <label 
                key={option.value} 
                className={`checkbox-item ${hasSubejes ? 'has-tooltip' : ''}`}
                onMouseEnter={(e) => {
                  if (hasSubejes && !isMobileOrTablet) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const tooltipWidth = 300;
                    const spacing = 10;
                    
                    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    let top = rect.bottom + spacing;
                    
                    if (left + tooltipWidth > window.innerWidth) {
                      left = window.innerWidth - tooltipWidth - 10;
                    }
                    if (left < 10) {
                      left = 10;
                    }
                    
                    const tooltipHeight = 200;
                    if (top + tooltipHeight > window.innerHeight) {
                      top = rect.top - tooltipHeight - spacing;
                    }
                    if (top < 10) {
                      top = 10;
                    }
                    
                    setVisibleTooltip(index);
                    setTooltipPosition({ left, top });
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobileOrTablet) {
                    setVisibleTooltip(null);
                    setTooltipPosition(null);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    if (readOnly) return;
                    const currentAxes = speaker.thematicAxes || [];
                    if (e.target.checked) {
                      onUpdate(speaker.id, 'thematicAxes', [...currentAxes, option.value]);
                    } else {
                      onUpdate(speaker.id, 'thematicAxes', currentAxes.filter(axis => axis !== option.value));
                    }
                  }}
                  disabled={readOnly}
                />
                <span className="checkbox-label">{option.label}</span>
                {hasSubejes && isMobileOrTablet && (
                  <span
                    className="modalidad-info-icon"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      border: '1px solid #d1d5db',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      marginLeft: '8px',
                      display: 'flex'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      
                      if (visibleTooltip === index) {
                        setVisibleTooltip(null);
                        setTooltipPosition(null);
                        return;
                      }
                      
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 300;
                      const spacing = 10;
                      
                      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                      let top = rect.bottom + spacing;
                      
                      if (left + tooltipWidth > window.innerWidth) {
                        left = window.innerWidth - tooltipWidth - 10;
                      }
                      if (left < 10) {
                        left = 10;
                      }
                      
                      const tooltipHeight = 200;
                      if (top + tooltipHeight > window.innerHeight) {
                        top = rect.top - tooltipHeight - spacing;
                      }
                      if (top < 10) {
                        top = 10;
                      }
                      
                      setVisibleTooltip(index);
                      setTooltipPosition({ left, top });
                    }}
                  >
                    ?
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </FormField>

      <div style={{ marginTop: '20px' }}>
      <FormField label="Breve descripción del contenido que desea abordar" required>
        <Textarea
          value={speaker.contentDescription || ''}
          onChange={(e) => onUpdate(speaker.id, 'contentDescription', e.target.value)}
          placeholder="Descripción del contenido..."
          rows={4}
          maxLength={700}
          showCharCount={true}
          disabled={readOnly}
        />
      </FormField>
      </div>

      <FormField 
        label="Documentos de apoyo"
        className="speaker-support-docs-field"
      >
        <div className="file-upload-container">
          {filePreviews.length > 0 ? (
            <div className="files-list">
              {filePreviews.map((preview, index) => (
                <div key={index} className="file-preview">
                  <p className="file-preview-text">{preview.name}</p>
                  {!readOnly && (
                    <button
                      type="button"
                      className="remove-file-button"
                      onClick={() => handleRemoveFile(index)}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="file-placeholder">
              <span>📄</span>
              <p>Sin archivos</p>
            </div>
          )}
          {!readOnly && (
            <label className="file-upload-btn">
              <input
                type="file"
                multiple
                onChange={(e) => handleFileChange(e.target.files)}
                style={{ display: 'none' }}
              />
              Agregar Archivos
            </label>
          )}
        </div>
      </FormField>

      {/* Checkbox Solicitar Aprobación */}
      <div style={{ marginTop: '20px' }}>
        <div className="checkbox-group">
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={speaker.requestApproval || false}
              onChange={(e) => {
                if (!readOnly) {
                  onUpdate(speaker.id, 'requestApproval', e.target.checked);
                }
              }}
              disabled={readOnly}
            />
            <span className="checkbox-label">Solicitar aprobación del orador</span>
          </label>
        </div>
      </div>
      
      {/* Badges de estado al final (solo en readonly del popup) */}
      {readOnly && showApprovalBadge && (() => {
        const statusInfo = getSpeakerStatusHelper(speaker);
        // Solo mostrar selector de aprobación si está completo o tiene solicitud de aprobación
        const shouldShowApprovalSelector = statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval;
        
        return (
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
              {/* Badge de estado de completitud - siempre se muestra si corresponde */}
              <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
              </span>
              {/* Badge de solicitado - siempre se muestra si corresponde */}
              {statusInfo.hasRequestApproval && (
                <span className="participation-status-badge status-requested">
                  <AlertTriangleIcon width={12} height={12} /> Solicitado
                </span>
              )}
              {/* Selector de estado de aprobación - solo se muestra si está completo o tiene solicitud */}
              {shouldShowApprovalSelector && onApprovalStatusChange && (
                <div style={{ minWidth: '120px', maxWidth: '140px', flexShrink: 0 }}>
                  <CustomSelect
                    className="admin-filter-select admin-filter-select-small"
                    value={speaker.approval_status || 'pending'}
                    onChange={(newStatus) => {
                      if (onApprovalStatusChange) {
                        onApprovalStatusChange(speaker.id, newStatus as 'pending' | 'approved' | 'rejected');
                      }
                    }}
                    options={[
                      { value: 'pending', label: 'Pendiente', color: '#3b82f6' },
                      { value: 'approved', label: 'Aprobado', color: '#10b981' },
                      { value: 'rejected', label: 'Rechazado', color: '#ef4444' }
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })()}
      </div>
      )}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={() => {
          onRemove(speaker.id);
          setShowDeleteModal(false);
        }}
        confirmText="Aceptar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de que deseas eliminar este orador?</p>
      </Modal>
    </div>
  );
};

const SpeakersSection: React.FC<SpeakersSectionProps> = ({ speakers, onChange, readOnly = false, hideTitle = false, initialExpandedSpeakerId, showApprovalBadge = false, onApprovalStatusChange, userId }) => {
  const MAX_SPEAKERS = 5;
  const [newSpeakerIds, setNewSpeakerIds] = useState<Set<string>>(new Set());

  // Validar y limpiar los datos de oradores
  const validSpeakers = React.useMemo(() => {
    if (!speakers || !Array.isArray(speakers)) {
      return [];
    }
    return speakers.filter(speaker => {
      // Validar que el speaker tenga al menos un id
      return speaker && speaker.id;
    }).map(speaker => ({
      ...speaker,
      id: speaker.id || Date.now().toString(),
      firstName: speaker.firstName || '',
      lastName: speaker.lastName || '',
      nationality: speaker.nationality || '',
      position: speaker.position || '',
      organization: speaker.organization || '',
      description: speaker.description || '',
      biography: speaker.biography || '',
      linkedin: speaker.linkedin || '',
      instagram: speaker.instagram || '',
      twitter: speaker.twitter || '',
      photo: speaker.photo || undefined,
      organizationLogo: speaker.organizationLogo || undefined,
      thematicAxes: speaker.thematicAxes || [],
      contentDescription: speaker.contentDescription || '',
      supportDocuments: Array.isArray(speaker.supportDocuments) ? speaker.supportDocuments : (speaker.supportDocuments ? [speaker.supportDocuments] : undefined),
      requestApproval: speaker.requestApproval || false
    }));
  }, [speakers]);

  const addSpeaker = () => {
    if (validSpeakers.length >= MAX_SPEAKERS) {
      return;
    }
    const newId = Date.now().toString();
    setNewSpeakerIds(prev => new Set(prev).add(newId));
    const newSpeaker: Speaker = {
      id: newId,
      firstName: '',
      lastName: '',
      nationality: '',
      position: '',
      organization: '',
      description: '',
      biography: '',
      linkedin: '',
      instagram: '',
      twitter: '',
      thematicAxes: [],
      contentDescription: '',
      supportDocuments: [],
      requestApproval: false
    };
    onChange([...validSpeakers, newSpeaker]);
  };

  const updateSpeaker = (id: string, field: keyof Speaker, value: any) => {
    onChange(validSpeakers.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const removeSpeaker = (id: string) => {
    onChange(validSpeakers.filter(s => s.id !== id));
  };

  return (
    <FormSection 
      title={hideTitle ? undefined : (
        <>
          Oradores en congreso <span style={{ fontSize: '14px', fontWeight: 400, color: '#64748b' }}>(Sujeto a categoría de participación / Requiere aprobación)</span>
        </>
      )}
    >
      {validSpeakers.map((speaker) => (
        <SpeakerCard
          key={speaker.id}
          speaker={speaker}
          onUpdate={updateSpeaker}
          onRemove={removeSpeaker}
          readOnly={readOnly}
          initialExpanded={newSpeakerIds.has(speaker.id) || (initialExpandedSpeakerId !== undefined && speaker.id === initialExpandedSpeakerId)}
          showApprovalBadge={showApprovalBadge}
          onApprovalStatusChange={onApprovalStatusChange}
          userId={userId}
        />
      ))}

      {validSpeakers.length < MAX_SPEAKERS && !readOnly && (
        <button 
          type="button" 
          className="add-button" 
          onClick={addSpeaker}
          style={{ marginTop: '0px' }}
        >
          + Agregar Orador
        </button>
      )}
      {validSpeakers.length >= MAX_SPEAKERS && (
        <p className="max-speakers-message">
          Se ha alcanzado el máximo de {MAX_SPEAKERS} oradores permitidos.
        </p>
      )}
    </FormSection>
  );
};

export default SpeakersSection;
